import React, { Component } from 'react'
import PropTypes from 'prop-types'
import OAuthScopes from './OAuthScopes'
import { Input, Button } from '@driftt/tide-core';
import { Table } from '@driftt/tide-table'
import { Icon } from '@driftt/tide-icons'
import { fetchAppRedirectUrls, updateAppRedirectUrls } from 'api/developerApi'
import isURL from 'validator/lib/isURL'
import cx from 'classnames'
import dividerSrc from '../divider.svg'
import 'styles/input.css'
import './style.css'
import { prependHTTPSAlways } from 'utils/validation'

const DEFAULT_URL = 'https://your.redirect.url'

const validateUrl = (value) => {
  const constraints = { protocols: ['https'], require_protocol: true }
  return isURL(value, constraints)
}

class OAuth extends Component {

  static propTypes = {
    appId: PropTypes.string.isRequired,
    showErrorNotification: PropTypes.func.isRequired
  }

  state = {
    inputRedirectUrl: '',
    storedRedirectUrls: [],
    isFetchingUrl: false,
    isSavingUrl: false
  }

  componentDidMount() {
    this.fetchRedirectUrls()
  }

  fetchRedirectUrls = () => {
    const onSuccess = (redirectUrls) => this.setState({ storedRedirectUrls: redirectUrls })
    const onError = (error) => this.props.showErrorNotification('We couldn’t fetch your redirect URLs. Please refresh the page.')
    const onFinally = () => this.setState({ isFetchingUrl: false })

    const onStateSet = () => fetchAppRedirectUrls(this.props.appId)
      .then(onSuccess)
      .catch(onError)
      .then(onFinally)

    this.setState({ isFetchingUrl: true }, onStateSet)
  }

  addRedirectUrl = () => {
    const { storedRedirectUrls, inputRedirectUrl } = this.state

    let updatedRedirectUrls = storedRedirectUrls
    this.removeDefault(updatedRedirectUrls)
    updatedRedirectUrls.push(inputRedirectUrl)
    this.updateRedirectUrls(updatedRedirectUrls)
  }

  removeDefault = (redirectUrls) => {
    if(redirectUrls.includes(DEFAULT_URL)) {
      return redirectUrls.splice(redirectUrls.indexOf(DEFAULT_URL), 1)
    }
    return redirectUrls
  }

  removeRedirectUrl = (index) => {
    const { storedRedirectUrls } = this.state

    let updatedRedirectUrls = storedRedirectUrls
    updatedRedirectUrls.splice(index, 1)
    this.updateRedirectUrls(updatedRedirectUrls)
  }

  updateRedirectUrls = (updatedRedirectUrls) => {
    const { appId } = this.props

    const onSuccess = (redirectUrls) => this.setState({ storedRedirectUrls: redirectUrls, inputRedirectUrl: "" })
    const onError = (error) => this.props.showErrorNotification('We couldn’t update your redirect URLs. Please try again.')
    const onFinally = () => this.setState({ isSavingUrl: false })

    updateAppRedirectUrls({ redirectUris: updatedRedirectUrls, appId })
      .then(onSuccess)
      .catch(onError)
      .then(onFinally)
  }

  onAddRedirectUrlClicked = () => {
    if (this.state.isSavingUrl) return
    this.setState({ isSavingUrl: true }, this.addRedirectUrl)
  }

  formatRedirectUrl = ({ target: { value = '' }}) => this.setState({ inputRedirectUrl: prependHTTPSAlways(value) })

  canAddRedirectUrl = () => {
    const { inputRedirectUrl, storedRedirectUrls } = this.state
    const isValidUrl = validateUrl(inputRedirectUrl)
    return isValidUrl && (!storedRedirectUrls.includes(inputRedirectUrl))
  }

  handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.addRedirectUrl()
    }
  }

  createTableColumns = () => ([
    { label: 'Your URLs', width: '90%' },
    { label: '', width: '15px' }
  ])

  createTableRow = (redirectUrl, index) => ({
    className: 'event-table-row',
    data: [
      { content: <span className="oauth-redirect-url-item">{redirectUrl}</span> },
      { content: this.state.storedRedirectUrls.length > 1 && <div className="delete-button" onClick={() => this.removeRedirectUrl(index)} >
        <Icon className="delete-icon" width={15} name="trash"/>
      </div> },
    ]
  })

  render() {
    const { storedRedirectUrls, inputRedirectUrl, isFetchingUrl, isSavingUrl } = this.state
    const { showErrorNotification, appId } = this.props

    return (
      <div className="app-oauth">
        <span className="app-oauth-title">
          OAuth & Scopes
        </span>
        <div className="app-oauth-redirects">
          <span className="app-oauth-section-title">
            Allowed Oauth Redirect URL(s)
          </span>
          <p className="app-oauth-section-description">
            You will need to configure a secure redirect URL in order to list your app publicly in the Drift App Directory.
            If you pass a URL in an OAuth request, it must (partially) match one of the HTTPS URLs you enter here.
            If you do not pass a URL, we will redirect to the first URL in the list.

            Note that you do not need to set up redirect URLs if this app is only intended for use in your own Drift org.
          </p>

          <Table
            className="redirect-urls-table"
            columns={this.createTableColumns()}
            data={storedRedirectUrls.map((redirectUrl, index) => this.createTableRow(redirectUrl, index))}
          />

          <Input
            label="Add Redirect URL"
            description="Add any redirect URLs where your app can receive authorization codes"
            value={inputRedirectUrl}
            onChange={this.formatRedirectUrl}
            onKeyDown={(event) => this.canAddRedirectUrl() && this.handleKeyDown(event)}
            className={cx('app-oauth-redirect-url-input', {
              'drift-input-loading': isFetchingUrl,
              'drift-input-loaded': !isFetchingUrl,
            })}
          />

          <Button
            type="primary"
            size="medium"
            className="app-oauth-redirect-add-url"
            disabled={!this.canAddRedirectUrl()}
            loading={isSavingUrl}
            onClick={this.onAddRedirectUrlClicked}>
            Add
          </Button>

        </div>

        <img
          className="app-oauth-divider"
          src={dividerSrc}
          alt="divider"
        />

        <OAuthScopes
          appId={appId}
          showErrorNotification={showErrorNotification}
        />

      </div>
    )
  }
}

export default OAuth
